import { useMemo } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import {
  DollarSign,
  Tag,
  Share2,
  TrendingUp,
  Award,
  Target,
  BarChart3,
} from "lucide-react";

const H1 = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 1.5rem;
`;

const Grid4 = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled.div`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  padding: 1.25rem;
`;

const IconBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  background: ${({ $tone, theme }) => {
    if ($tone === "accent") return `hsl(${theme.colors.accent} / 0.1)`;
    if ($tone === "maroon") return `hsl(${theme.colors.couponMaroon} / 0.1)`;
    return `hsl(${theme.colors.primary} / 0.1)`;
  }};
  color: ${({ $tone, theme }) => {
    if ($tone === "accent") return `hsl(${theme.colors.accent})`;
    if ($tone === "maroon") return `hsl(${theme.colors.couponMaroon})`;
    return `hsl(${theme.colors.primary})`;
  }};
`;

const StatLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

const StatNum = styled.p`
  margin: 0.25rem 0 0;
  font-size: 1.5rem;
  font-weight: 800;
`;

const StatHint = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

const TwoCol = styled.div`
  display: grid;
  gap: 1.5rem;
  margin-bottom: 2rem;
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Card = styled.div`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  padding: 1.25rem;
`;

const CardTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid hsl(${({ theme }) => theme.colors.border});
  &:last-child {
    border-bottom: none;
  }
`;

const Bar = styled.div`
  height: 0.5rem;
  background: hsl(${({ theme }) => theme.colors.muted});
  border-radius: 9999px;
  overflow: hidden;
  margin-top: 0.375rem;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 9999px;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $color, theme }) => `hsl(${theme.colors[$color]})`};
`;

const BadgeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Badge = styled.div`
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid
    ${({ $earned, theme }) =>
      $earned ? `hsl(${theme.colors.accent} / 0.3)` : `hsl(${theme.colors.border})`};
  padding: 0.75rem;
  text-align: center;
  opacity: ${({ $earned }) => ($earned ? 1 : 0.5)};
  background: ${({ $earned, theme }) =>
    $earned ? `hsl(${theme.colors.accent} / 0.05)` : `hsl(${theme.colors.muted} / 0.3)`};
`;

const Empty = styled.p`
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  font-size: 0.9375rem;
`;

function pct(n, cap = 10) {
  return Math.min(Math.round((n / cap) * 100), 100);
}

export default function DashboardOverview() {
  const { isAuthenticated } = useSelector((s) => s.auth);
  const { savings, myComments } = useSelector((s) => s.user);
  const { likedCoupons } = useSelector((s) => s.couponLike);

  const totalSaved = useMemo(
    () => savings.reduce((sum, s) => sum + (Number(s.amount) || 0), 0),
    [savings]
  );
  const couponsUsed = savings.length;
  const commentsCount = myComments.length;
  const likedCount = likedCoupons.length;

  const recentSavings = savings.slice(0, 3);

  const badges = useMemo(
    () => [
      {
        title: "First Save",
        desc: "Used your first coupon",
        earned: couponsUsed >= 1,
      },
      {
        title: "Deal Hunter",
        desc: "Used 10+ coupons",
        earned: couponsUsed >= 10,
      },
      {
        title: "Super Saver",
        desc: "Saved over $500",
        earned: totalSaved >= 500,
      },
      {
        title: "Commenter",
        desc: "Posted 5+ comments",
        earned: commentsCount >= 5,
      },
      {
        title: "Curator",
        desc: "Liked 10+ deals",
        earned: likedCount >= 10,
      },
      {
        title: "Money Master",
        desc: "Saved over $1000",
        earned: totalSaved >= 1000,
      },
      {
        title: "Community voice",
        desc: "Posted 15+ comments",
        earned: commentsCount >= 15,
      },
      {
        title: "Saver pro",
        desc: "20+ savings entries",
        earned: couponsUsed >= 20,
      },
    ],
    [couponsUsed, totalSaved, commentsCount, likedCount]
  );

  if (!isAuthenticated) {
    return (
      <div>
        <H1>Dashboard Overview</H1>
        <Empty>Sign in to see your overview.</Empty>
      </div>
    );
  }

  return (
    <div>
      <H1>Dashboard Overview</H1>
      <Grid4>
        <StatCard>
          <IconBox>
            <DollarSign size={20} />
          </IconBox>
          <StatLabel>Total Saved</StatLabel>
          <StatNum>${totalSaved.toFixed(2)}</StatNum>
          <StatHint>From logged savings</StatHint>
        </StatCard>
        <StatCard>
          <IconBox $tone="accent">
            <Tag size={20} />
          </IconBox>
          <StatLabel>Savings entries</StatLabel>
          <StatNum>{couponsUsed}</StatNum>
          <StatHint>Rows in your savings log</StatHint>
        </StatCard>
        <StatCard>
          <IconBox $tone="maroon">
            <Share2 size={20} />
          </IconBox>
          <StatLabel>Liked deals</StatLabel>
          <StatNum>{likedCount}</StatNum>
          <StatHint>From your saved coupons</StatHint>
        </StatCard>
        <StatCard>
          <IconBox>
            <Target size={20} />
          </IconBox>
          <StatLabel>Comments</StatLabel>
          <StatNum>{commentsCount}</StatNum>
          <StatHint>On coupon pages</StatHint>
        </StatCard>
      </Grid4>

      <TwoCol>
        <Card>
          <CardTitle>
            <TrendingUp size={16} /> Recent savings
          </CardTitle>
          {recentSavings.length === 0 ? (
            <Empty style={{ margin: 0 }}>No savings entries yet.</Empty>
          ) : (
            recentSavings.map((item) => (
              <Row key={item.id}>
                <div>
                  <p style={{ margin: 0, fontSize: "0.875rem", fontWeight: 500 }}>
                    {item.label || "Entry"}
                  </p>
                  <p style={{ margin: 0, fontSize: "0.75rem", color: "hsl(220 10% 46%)" }}>
                    {item.note || "Recorded saving"}
                  </p>
                </div>
                <span style={{ fontWeight: 700, color: "hsl(43 80% 52%)" }}>
                  {item.currency || "USD"} {Number(item.amount).toFixed(2)}
                </span>
              </Row>
            ))
          )}
        </Card>
        <Card>
          <CardTitle>
            <BarChart3 size={16} /> Activity summary
          </CardTitle>
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.875rem",
                marginBottom: 6,
              }}
            >
              <span style={{ color: "hsl(220 10% 46%)" }}>Comments</span>
              <strong>{commentsCount}</strong>
            </div>
            <Bar>
              <BarFill $pct={pct(commentsCount, 15)} $color="primary" />
            </Bar>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.875rem",
                marginBottom: 6,
              }}
            >
              <span style={{ color: "hsl(220 10% 46%)" }}>Reviews</span>
              <strong>0</strong>
            </div>
            <Bar>
              <BarFill $pct={0} $color="accent" />
            </Bar>
          </div>
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.875rem",
                marginBottom: 6,
              }}
            >
              <span style={{ color: "hsl(220 10% 46%)" }}>Liked coupons</span>
              <strong>{likedCount}</strong>
            </div>
            <Bar>
              <BarFill $pct={pct(likedCount, 10)} $color="couponMaroon" />
            </Bar>
          </div>
        </Card>
      </TwoCol>

      <Card>
        <CardTitle>
          <Award size={16} style={{ color: "hsl(43 80% 52%)" }} /> Achievements
        </CardTitle>
        <BadgeGrid>
          {badges.map((b) => (
            <Badge key={b.title} $earned={b.earned}>
              <Award
                size={16}
                style={{
                  margin: "0 auto 0.5rem",
                  display: "block",
                  color: b.earned ? "hsl(43 80% 52%)" : "hsl(220 10% 46%)",
                }}
              />
              <p style={{ margin: 0, fontSize: "0.75rem", fontWeight: 600 }}>{b.title}</p>
              <p style={{ margin: "0.25rem 0 0", fontSize: "0.625rem", color: "hsl(220 10% 46%)" }}>
                {b.desc}
              </p>
            </Badge>
          ))}
        </BadgeGrid>
      </Card>
    </div>
  );
}
